import { supabase } from '../lib/supabase';

/**
 * Service for automated barrel replenishment and supplier management
 */

export const barrelReplenishmentService = {
  /**
   * Fetch all suppliers with optional filters
   */
  async getSuppliers(filters = {}) {
    try {
      let query = supabase
        ?.from('barrel_suppliers')
        ?.select('*')
        ?.order('name', { ascending: true });

      if (filters?.isActive !== undefined) {
        query = query?.eq('is_active', filters?.isActive);
      }

      if (filters?.city) {
        query = query?.eq('city', filters?.city);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      throw error;
    }
  },

  /**
   * Create new supplier
   */
  async createSupplier(supplierData) {
    try {
      const { data, error } = await supabase
        ?.from('barrel_suppliers')
        ?.insert(supplierData)
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating supplier:', error);
      throw error;
    }
  },

  /**
   * Update supplier
   */
  async updateSupplier(id, updates) {
    try {
      const { data, error } = await supabase
        ?.from('barrel_suppliers')
        ?.update(updates)
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating supplier:', error);
      throw error;
    }
  },

  /**
   * Delete supplier
   */
  async deleteSupplier(id) {
    try {
      const { error } = await supabase
        ?.from('barrel_suppliers')
        ?.delete()
        ?.eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting supplier:', error);
      throw error;
    }
  },

  /**
   * Fetch replenishment rules
   */
  async getReplenishmentRules() {
    try {
      const { data, error } = await supabase
        ?.from('barrel_replenishment_rules')
        ?.select('*, barrel_suppliers(*)')
        ?.order('barrel_type', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching replenishment rules:', error);
      throw error;
    }
  },

  /**
   * Update replenishment rule
   */
  async updateReplenishmentRule(id, updates) {
    try {
      const { data, error } = await supabase
        ?.from('barrel_replenishment_rules')
        ?.update(updates)
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating replenishment rule:', error);
      throw error;
    }
  },

  /**
   * Fetch purchase orders with optional filters
   */
  async getPurchaseOrders(filters = {}) {
    try {
      let query = supabase
        ?.from('barrel_purchase_orders')
        ?.select('*, barrel_suppliers(*)')
        ?.order('order_date', { ascending: false });

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }

      if (filters?.supplierId) {
        query = query?.eq('supplier_id', filters?.supplierId);
      }

      if (filters?.startDate) {
        query = query?.gte('order_date', filters?.startDate);
      }

      if (filters?.endDate) {
        query = query?.lte('order_date', filters?.endDate);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching purchase orders:', error);
      throw error;
    }
  },

  /**
   * Get single purchase order by ID
   */
  async getPurchaseOrderById(id) {
    try {
      const { data, error } = await supabase
        ?.from('barrel_purchase_orders')
        ?.select('*, barrel_suppliers(*)')
        ?.eq('id', id)
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching purchase order:', error);
      throw error;
    }
  },

  /**
   * Create purchase order
   */
  async createPurchaseOrder(orderData) {
    try {
      // Generate PO number
      const { data: poNumber, error: poError } = await supabase
        ?.rpc('generate_po_number');

      if (poError) throw poError;

      const { data, error } = await supabase
        ?.from('barrel_purchase_orders')
        ?.insert({
          ...orderData,
          po_number: poNumber
        })
        ?.select('*, barrel_suppliers(*)')
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating purchase order:', error);
      throw error;
    }
  },

  /**
   * Update purchase order
   */
  async updatePurchaseOrder(id, updates) {
    try {
      const { data, error } = await supabase
        ?.from('barrel_purchase_orders')
        ?.update(updates)
        ?.eq('id', id)
        ?.select('*, barrel_suppliers(*)')
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating purchase order:', error);
      throw error;
    }
  },

  /**
   * Delete purchase order
   */
  async deletePurchaseOrder(id) {
    try {
      const { error } = await supabase
        ?.from('barrel_purchase_orders')
        ?.delete()
        ?.eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting purchase order:', error);
      throw error;
    }
  },

  /**
   * Check stock levels and get replenishment recommendations
   */
  async checkStockLevels() {
    try {
      const { data, error } = await supabase
        ?.rpc('check_barrel_stock_and_trigger_replenishment');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error checking stock levels:', error);
      throw error;
    }
  },

  /**
   * Fetch replenishment triggers
   */
  async getReplenishmentTriggers(filters = {}) {
    try {
      let query = supabase
        ?.from('barrel_replenishment_triggers')
        ?.select('*, barrel_purchase_orders(po_number, status)')
        ?.order('triggered_at', { ascending: false });

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }

      if (filters?.triggerType) {
        query = query?.eq('trigger_type', filters?.triggerType);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching replenishment triggers:', error);
      throw error;
    }
  },

  /**
   * Create replenishment trigger
   */
  async createReplenishmentTrigger(triggerData) {
    try {
      const { data, error } = await supabase
        ?.from('barrel_replenishment_triggers')
        ?.insert(triggerData)
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating replenishment trigger:', error);
      throw error;
    }
  },

  /**
   * Update replenishment trigger
   */
  async updateReplenishmentTrigger(id, updates) {
    try {
      const { data, error } = await supabase
        ?.from('barrel_replenishment_triggers')
        ?.update(updates)
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating replenishment trigger:', error);
      throw error;
    }
  },

  /**
   * Get replenishment statistics
   */
  async getReplenishmentStatistics() {
    try {
      const [ordersResult, triggersResult, suppliersResult] = await Promise.all([
        supabase?.from('barrel_purchase_orders')?.select('status, total_amount, expected_delivery_date'),
        supabase?.from('barrel_replenishment_triggers')?.select('status, trigger_type'),
        supabase?.from('barrel_suppliers')?.select('is_active, quality_rating, reliability_score')
      ]);

      const orders = ordersResult?.data || [];
      const triggers = triggersResult?.data || [];
      const suppliers = suppliersResult?.data || [];

      return {
        totalOrders: orders?.length,
        pendingOrders: orders?.filter(o => o?.status === 'pending')?.length,
        approvedOrders: orders?.filter(o => o?.status === 'approved')?.length,
        inTransitOrders: orders?.filter(o => o?.status === 'in_transit')?.length,
        deliveredOrders: orders?.filter(o => o?.status === 'delivered')?.length,
        totalOrderValue: orders?.reduce((sum, order) => sum + parseFloat(order?.total_amount || 0), 0),
        activeTriggers: triggers?.filter(t => t?.status === 'pending' || t?.status === 'processing')?.length,
        completedTriggers: triggers?.filter(t => t?.status === 'completed')?.length,
        failedTriggers: triggers?.filter(t => t?.status === 'failed')?.length,
        activeSuppliers: suppliers?.filter(s => s?.is_active)?.length,
        averageQualityRating: suppliers?.reduce((sum, s) => sum + parseFloat(s?.quality_rating || 0), 0) / (suppliers?.length || 1),
        averageReliabilityScore: suppliers?.reduce((sum, s) => sum + parseFloat(s?.reliability_score || 0), 0) / (suppliers?.length || 1)
      };
    } catch (error) {
      console.error('Error fetching replenishment statistics:', error);
      throw error;
    }
  },

  /**
   * Get supplier performance metrics
   */
  async getSupplierPerformance(supplierId) {
    try {
      const { data, error } = await supabase
        ?.from('barrel_purchase_orders')
        ?.select('*')
        ?.eq('supplier_id', supplierId);

      if (error) throw error;

      const orders = data || [];
      const deliveredOrders = orders?.filter(o => o?.status === 'delivered');
      const onTimeDeliveries = deliveredOrders?.filter(o => {
        if (!o?.expected_delivery_date || !o?.actual_delivery_date) return false;
        return new Date(o?.actual_delivery_date) <= new Date(o?.expected_delivery_date);
      });

      return {
        totalOrders: orders?.length,
        deliveredOrders: deliveredOrders?.length,
        onTimeDeliveryRate: deliveredOrders?.length > 0 ? (onTimeDeliveries?.length / deliveredOrders?.length) * 100 : 0,
        averageDeliveryTime: this.calculateAverageDeliveryTime(deliveredOrders),
        totalSpent: orders?.reduce((sum, order) => sum + parseFloat(order?.total_amount || 0), 0)
      };
    } catch (error) {
      console.error('Error fetching supplier performance:', error);
      throw error;
    }
  },

  /**
   * Calculate average delivery time
   */
  calculateAverageDeliveryTime(orders) {
    const validOrders = orders?.filter(o => o?.order_date && o?.actual_delivery_date);
    if (validOrders?.length === 0) return 0;

    const totalDays = validOrders?.reduce((sum, order) => {
      const orderDate = new Date(order?.order_date);
      const deliveryDate = new Date(order?.actual_delivery_date);
      const diffTime = Math.abs(deliveryDate - orderDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return sum + diffDays;
    }, 0);

    return totalDays / validOrders?.length;
  },

  /**
   * Subscribe to purchase order changes
   */
  subscribeToPurchaseOrders(callback) {
    const subscription = supabase
      ?.channel('barrel_purchase_orders_changes')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'barrel_purchase_orders'
        },
        callback
      )
      ?.subscribe();

    return subscription;
  },

  /**
   * Subscribe to replenishment trigger changes
   */
  subscribeToReplenishmentTriggers(callback) {
    const subscription = supabase
      ?.channel('barrel_replenishment_triggers_changes')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'barrel_replenishment_triggers'
        },
        callback
      )
      ?.subscribe();

    return subscription;
  }
};

export default barrelReplenishmentService;